/* eslint-disable react/jsx-props-no-spreading */
import {
  Box,
  Circle,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { CiCircleCheck, CiCirclePlus } from 'react-icons/ci';

import CategoriesService from '@/client/services/api/CategoriesService';
import type { Category } from '@/client/types/Category';
import GroupsService from '@/client/services/api/admin/connect/GroupsService';
import NoSearchResultsListItem from '@/client/components/admin/create/content/lists/NoSearchResultsListItem';
import { contentTypeIconMap } from '@/client/utils/type-maps/contentTypeIconMap';
import { useAuthStore } from '@/client/services/state/authStore';
import { useCombobox } from 'downshift';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SegmentationSearchProps {
  handleSelectedItemChangeFn: (selectedItem: Category) => void;
  handleCheckIsSelectedFn: (selectedItem: Category) => boolean;
}

export default function SegmentationSearch({
  handleSelectedItemChangeFn,
  handleCheckIsSelectedFn,
}: SegmentationSearchProps) {
  const { t } = useTranslation();
  const { authConfig } = useAuthStore();
  const { company } = authConfig;
  const groupsEnabled = company.groups;
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);

  const search = async (query: string | undefined) => {
    if (!query) return;

    const promises: any = [CategoriesService.searchCategories(query)];

    if (groupsEnabled) {
      promises.push(GroupsService.simpleSearch(query, { pageIndex: 1, pageSize: 50 }));
    }

    const [categories, groups] = await Promise.all(promises);

    // ! Convert _id to id for use with GraphQL
    const convertedCategories = categories.map((section: any) => ({
      ...section,
      options: section.options.map((option: any) => ({
        ...option,
        id: option._id,
      })),
    }));

    const convertedGroups =
      groups?.map((group: any) => ({
        ...group,
        id: group._id,
      })) || [];

    if (groupsEnabled && groups) {
      return [convertedCategories, convertedGroups];
    }

    return [convertedCategories];
  };

  const { isLoading, isFetching, isError, data } = useQuery({
    enabled: !!searchTerm,
    queryKey: ['authoringSegmentationSearch', searchTerm],
    queryFn: () => search(searchTerm),
    select: (selectData) => {
      if (!selectData) return [];

      const filteredCategories = selectData[0].filter(
        (category: any) => category.options.length > 0,
      );

      if (selectData[1] && selectData[1].length > 0) {
        return [
          ...filteredCategories,
          {
            title: 'Groups',
            options: selectData[1].map((group: any) => ({
              ...group,
              type: 'group',
            })),
          },
        ];
      }

      return [...filteredCategories];
    },
  });

  const flattenGroupOptions = (options: { title: string; options: Category[] }[]) =>
    options.reduce((prev: any, curr: any) => [...prev, ...curr.options], []);

  const { getMenuProps, getInputProps, highlightedIndex, getItemProps } = useCombobox({
    onInputValueChange({ inputValue }) {
      setSearchTerm(inputValue);
    },
    items: data ? flattenGroupOptions(data) : [],
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem) {
        return;
      }

      handleSelectedItemChangeFn(selectedItem);
    },
    selectedItem: null,
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true, // keep menu open after selection.
            highlightedIndex: state.highlightedIndex,
            inputValue: '', // don't add the item string as input value at selection.
          };
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            inputValue: '', // don't add the item string as input value at selection.
          };
        case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
          return {
            ...changes,
            inputValue: state.inputValue,
          };
        default:
          return changes;
      }
    },
  });

  return (
    <>
      <InputGroup>
        <Input
          variant="create"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={`${t('authoring.audience.segmentation.search.placeholder')}`}
          {...getInputProps()}
        />

        <InputRightElement>{isFetching && <Spinner size="sm" />}</InputRightElement>
      </InputGroup>

      <List
        marginTop={2}
        flexDirection="column"
        border={data ? '1px solid' : 'none'}
        display={data ? 'flex' : 'none'}
        borderColor="warmNeutral.500"
        borderRadius="md"
        background="white"
        overflow="scroll"
        paddingX={6}
        zIndex={9999}
        maxH={400}
        width="inherit"
        {...getMenuProps()}
      >
        {data?.length === 0 && <NoSearchResultsListItem />}

        {data &&
          data.length > 0 &&
          data.reduce(
            (results: any, section: any, sectionIndex: any) => {
              results.sections.push(
                <Box
                  key={`results_section_${sectionIndex + 1}`}
                  paddingY={4}
                  borderBottom="1px solid"
                  borderColor="warmNeutral.100"
                >
                  {section.options.length > 0 && (
                    <Text variant="createLabel" marginY={2}>
                      {section.title}
                    </Text>
                  )}

                  {section.options.length > 0 &&
                    section.options.map((option: Category, optionIndex: any) => {
                      // eslint-disable-next-line no-plusplus, no-param-reassign
                      const resultIndex = results.itemIndex++;

                      const isSelected = handleCheckIsSelectedFn(option);

                      return (
                        <ListItem
                          key={`option_index_${optionIndex + 1}`}
                          paddingX={4}
                          paddingY={2}
                          _hover={{
                            cursor: 'pointer',
                          }}
                          {...getItemProps({
                            item: option,
                            index: resultIndex,
                            'aria-selected': isSelected,
                          })}
                          backgroundColor={
                            highlightedIndex === resultIndex ? 'warmNeutral.0' : 'white'
                          }
                          border={highlightedIndex === resultIndex ? '1px solid' : 'none'}
                          borderColor="warmNeutral.400"
                          borderRadius="6px"
                        >
                          <Flex alignItems="center" width="full" justifyContent="space-between">
                            <Circle
                              backgroundColor={isSelected ? 'gray.50' : 'gray.100'}
                              size="30px"
                            >
                              <Icon
                                color={isSelected ? 'neutral.1000' : 'neutral.1200'}
                                as={contentTypeIconMap[option.type]}
                              />
                            </Circle>

                            <Box flexGrow={1} textAlign="left" paddingLeft={4}>
                              <Text
                                fontSize="sm"
                                fontWeight={500}
                                color={isSelected ? 'neutral.1000' : 'neutral.1200'}
                              >
                                {option.legacyName || option.name}
                              </Text>
                            </Box>

                            <Icon
                              boxSize={8}
                              as={isSelected ? CiCircleCheck : CiCirclePlus}
                              color={isSelected ? 'warmNeutral.300' : 'neutral.800'}
                            />
                          </Flex>
                        </ListItem>
                      );
                    })}
                </Box>,
              );

              return results;
            },
            { sections: [], itemIndex: 0 },
          ).sections}
      </List>
    </>
  );
}
